import { useState, type FormEvent } from "react";

const FORM_ID = "db6dtkbghk82orf0ckgg";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db6dtkbghk82orf0ckgg";

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      // 蜜罐命中：静默处理，展示与正常提交一致的成功反馈
      formEl.reset();
      setMessage("");
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value);
      if (str.trim() !== "") payload.append(key, str);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const code = parsed?.code;

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() !== ""
            ? serverMsg
            : "提交失败，请稍后重试",
        );
        return;
      }

      formEl.reset();
      setMessage("");
      setStatus("success");
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form={FORM_ID}
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200/70 p-6 md:p-8"
    >
      <h3 className="font-heading text-lg font-semibold text-foreground-950">意见反馈</h3>
      <p className="mt-1 text-sm text-foreground-600">欢迎留下你的建议，帮助我们完善内容与体验</p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fb-name" className="block text-sm font-label text-foreground-800 mb-1.5">
            称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            maxLength={50}
            placeholder="怎么称呼你"
            className="w-full h-11 px-3 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="block text-sm font-label text-foreground-800 mb-1.5">
            电子邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            maxLength={100}
            placeholder="name@example.com"
            className="w-full h-11 px-3 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-type" className="block text-sm font-label text-foreground-800 mb-1.5">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="category"
          defaultValue="内容建议"
          className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
        >
          <option value="内容建议">内容建议</option>
          <option value="内容纠错">内容纠错</option>
          <option value="功能体验">功能体验</option>
          <option value="合作洽谈">合作洽谈</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="block text-sm font-label text-foreground-800 mb-1.5">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的建议或问题（不超过 500 字）"
          onChange={(e) => setMessage(e.target.value)}
          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
        ></textarea>
        <p className="mt-1 text-right text-xs font-label text-foreground-500">{message.length}/500</p>
      </div>

      {/* 反垃圾字段（非必填，用户不可见） */}
      <div className="form-honeypot-field" aria-hidden="true">
        <label htmlFor="fb-website-alt">请勿填写此项</label>
        <input
          id="fb-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex items-center gap-2 h-11 px-8 rounded-md bg-accent-500 text-accent-950 font-label text-sm hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i> 提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i> 提交反馈
          </>
        )}
      </button>

      {status === "success" && (
        <p className="mt-4 flex items-center gap-2 text-sm font-label text-secondary-700">
          <i className="ri-checkbox-circle-line"></i> 感谢你的反馈，我们已收到！
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-center gap-2 text-sm font-label text-accent-700">
          <i className="ri-error-warning-line"></i> {formError}
        </p>
      )}
    </form>
  );
}